import { useEffect, useRef, useState } from "react";
import { YStack, XStack, Text, Stack, Input, TextArea, Spinner, Sheet, View } from "tamagui";
import { useForm } from "react-hook-form";
import { IoMdClose } from "react-icons/io";
import { RiMapPinLine } from "react-icons/ri";
import { colors } from "@styles/colors";
import SheetWrapper from "@components/wrapper/SheetWrapper";
import { zIndexes } from "@utils/zIndexLevel";
import MapPicker from "./MapPicker";
import { addressToGeocode, getCurrentLocation, combinedAddress, geocodeToAddress } from "./func/addressTool";
import { handleAPIGeocodeGetAddress } from "./API/handleAddressAPI";
import { cleanKeyword } from "./func/addressTextTool";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { appToast } from "@components/Toast";
import type { AddAddressFormData } from "./AddAddressFlowSheet";
import { isAddressDeliverable, type DeliveryArea } from "./func/isAddressDeliverable";

type AddrForm = {
  city: string;
  area: string;
  road: string;
  streetNumber: string;
  floor: string;
  room: string;
  otherAddress: string;
  latitude: number | null;
  longitude: number | null;
};

const EMPTY_FORM: AddrForm = {
  city: "",
  area: "",
  road: "",
  streetNumber: "",
  floor: "",
  room: "",
  otherAddress: "",
  latitude: null,
  longitude: null,
};

/** initialValues(Partial<AddAddressFormData>) → 內部 AddrForm，供編輯預填 */
const toAddrForm = (init?: Partial<AddAddressFormData>): AddrForm => ({
  city: init?.city ?? "",
  area: init?.area ?? "",
  road: init?.road ?? "",
  streetNumber: init?.streetNumber ?? "",
  floor: init?.floor ?? "",
  room: init?.room ?? "",
  otherAddress: init?.otherAddress ?? "",
  latitude: init?.latitude ?? null,
  longitude: init?.longitude ?? null,
});

const Field = ({
  label,
  value,
  onChange,
  placeholder,
  flex,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  flex?: number;
  required?: boolean;
}) => (
  <YStack flex={flex} gap={6}>
    <Text fontSize={12} color={colors.sub} fontWeight="600">
      {label}
      {required && <Text color={colors.danger}> *</Text>}
    </Text>
    <Input
      value={value}
      onChangeText={(t: string) => onChange(t)}
      placeholder={placeholder}
      borderWidth={1.5}
      borderColor={colors.border}
      borderRadius={11}
      backgroundColor="#fff"
      color={colors.text}
      fontSize={15}
      h={46}
    />
  </YStack>
);

const LocateButton = ({
  label,
  loading,
  onPress,
}: {
  label: string;
  loading: boolean;
  onPress: () => void;
}) => (
  <XStack
    flex={1}
    ai="center"
    jc="center"
    gap={7}
    py={11}
    borderRadius={12}
    backgroundColor={colors.primaryBg}
    cursor="pointer"
    onPress={onPress}
    pressStyle={{ opacity: 0.8 }}
  >
    {loading ? <Spinner size="small" color={colors.primary} /> : <RiMapPinLine size={16} color={colors.primary} />}
    <Text fontSize={13} fontWeight="700" color={colors.primary}>
      {label}
    </Text>
  </XStack>
);

interface AddressFormSheetProps {
  isOpen: boolean;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
  onSelect: (data: AddAddressFormData) => void | Promise<void>;
  title?: string;
  initialValues?: Partial<AddAddressFormData>;
  /**
   * 此瓦斯行的配送範圍。傳入時：選完縣市/區後即時檢查是否可配送，
   * 不可送則顯示警告文字並禁用儲存按鈕。未傳（如帳號頁/切換選單）→ 零行為差異。
   */
  deliveryAreas?: DeliveryArea[];
}

/**
 * 共用新增/編輯地址 modal：對齊 Kiosk 原生 KioskAddressSheet 的長相
 * （純文字輸入欄位 + 「使用目前位置 / 在地圖上定位」+ 拖曳地圖微調），但去掉 kiosk 配色、改走 app 樣式。
 * - initialValues 有值 → 預填現有地址（編輯送貨地點等情境）。
 * - 送出沿用 AddAddressFormData 形狀，交給上層建立 API（不變）。
 */
export default function AddressFormSheet({
  isOpen,
  onOpenChange,
  onSelect,
  title = "新增地址",
  initialValues,
  deliveryAreas,
}: AddressFormSheetProps) {
  const { lineAccessToken } = useLineAuth();
  const methods = useForm<AddrForm>({ defaultValues: EMPTY_FORM });
  const { setValue, watch, getValues, reset } = methods;
  const v = watch();
  // 配送範圍提示：僅在有傳 deliveryAreas 且已選 city+area 且該地址不可配送時為 true。
  /** MapPicker 定位中 → 卡控儲存，避免存到尚未更新的座標 */
  const [isMapGeocoding, setIsMapGeocoding] = useState(false);
  const outOfDeliveryRange = deliveryAreas !== undefined && !!v.city && !!v.area && !isAddressDeliverable(deliveryAreas, v.city, v.area);
  const [locating, setLocating] = useState(false);
  const [saving, setSaving] = useState(false);
  // 同步鎖：state 更新是非同步的，極快速連點時 saving 還來不及變 true，
  // ref 可在同一個 tick 內擋掉第二次送出，避免建立重複地址。
  const savingRef = useRef(false);

  const set = (k: keyof AddrForm) => (val: string) => setValue(k, val as never);

  useEffect(() => {
    if (isOpen) reset(toAddrForm(initialValues));
  }, [isOpen, initialValues, reset]);

  // 由座標反查地址並帶入欄位（失敗不擋，至少有座標）
  // 有 LINE token → 用正規化 API（台灣切分較準）；否則（含 dev）→ 用免授權的 Nominatim。
  const fillFieldsFromLatLng = async (lat: number, lng: number): Promise<boolean> => {
    if (lineAccessToken) {
      const geo = await handleAPIGeocodeGetAddress({ lat, lng }, lineAccessToken);
      const a = geo?.possibleList?.[0] as Record<string, string> | undefined;
      if (a) {
        if (a.administrative_area_level_1) setValue("city", a.administrative_area_level_1);
        if (a.administrative_area_level_2) setValue("area", a.administrative_area_level_2);
        if (a.route) setValue("road", a.route);
        if (a.street_number) setValue("streetNumber", cleanKeyword(a.street_number, "號"));
        return true;
      }
    }
    const r = await geocodeToAddress(lat, lng);
    if (!r) return false;
    if (r.city) setValue("city", r.city);
    if (r.area) setValue("area", r.area);
    if (r.road) setValue("road", r.road);
    if (r.streetNumber) setValue("streetNumber", cleanKeyword(r.streetNumber, "號"));
    return true;
  };

  const useGps = async () => {
    setLocating(true);
    const res = await getCurrentLocation();
    if (res.state !== "success") {
      setLocating(false);
      appToast.error(res.errorMsg || "定位失敗");
      return;
    }
    const lat = Number(res.lat);
    const lng = Number(res.lng);
    setValue("latitude", lat);
    setValue("longitude", lng);
    const filled = await fillFieldsFromLatLng(lat, lng);
    setLocating(false);
    appToast.success(filled ? "已帶入目前位置地址，可再微調" : "已定位目前位置，可在地圖微調");
  };

  // 漸進式地理編碼：Nominatim 對含門牌號的台灣地址常查不到，逐步放寬
  // （完整 → 去門牌 → 路名層級），讓圖釘至少落在路上，再由使用者拖曳微調。
  const geocodeProgressive = async (city: string, area: string, road: string, streetNumber: string) => {
    const queries = [
      combinedAddress({ city, area, road, streetNumber }),
      combinedAddress({ city, area, road }),
      `${city}${area}${road}`,
    ];
    const tried = new Set<string>();
    for (const q of queries) {
      if (!q || tried.has(q)) continue;
      tried.add(q);
      const res = await addressToGeocode(q);
      if (res.state === "success") return res;
    }
    return { state: "error" as const, lat: "", lng: "" };
  };

  const locateByAddress = async () => {
    const { city, area, road, streetNumber } = getValues();
    if (!city || !area || !road || !streetNumber) {
      appToast.error("請先填寫縣市、區、路名、門牌號");
      return;
    }
    setLocating(true);
    const res = await geocodeProgressive(city, area, road, streetNumber);
    setLocating(false);
    if (res.state === "success") {
      setValue("latitude", Number(res.lat));
      setValue("longitude", Number(res.lng));
      appToast.success("已在地圖定位，請拖曳圖釘微調到正確門口");
    } else {
      appToast.error("找不到此地址座標，請改用「目前位置」或拖曳地圖");
    }
  };

  const handleClose = () => onOpenChange(false);

  const handleSave = async () => {
    const d = getValues();
    if (!d.city || !d.area || !d.road || !d.streetNumber) {
      appToast.error("請填寫縣市、區、路名、門牌號");
      return;
    }
    // 配送範圍守門（與畫面禁用按鈕雙重保障）：所選地址不在此瓦斯行配送範圍 → 擋送出
    if (deliveryAreas !== undefined && !isAddressDeliverable(deliveryAreas, d.city, d.area)) {
      appToast.error(`此瓦斯行無法配送到${d.city}${d.area}`);
      return;
    }
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      let lat = d.latitude;
      let lng = d.longitude;
      if (lat == null || lng == null) {
        const res = await geocodeProgressive(d.city, d.area, d.road, d.streetNumber);
        if (res.state === "success") {
          lat = Number(res.lat);
          lng = Number(res.lng);
        }
      }
      await onSelect({
        city: d.city,
        area: d.area,
        road: d.road,
        streetNumber: d.streetNumber,
        floor: d.floor || undefined,
        room: d.room || undefined,
        otherAddress: d.otherAddress?.trim() || undefined,
        latitude: lat,
        longitude: lng,
      });
      handleClose();
    } catch (err: unknown) {
      appToast.error((err as Error)?.message || "儲存失敗，請稍後再試。");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <SheetWrapper
      sheetToTop
      zIndex={zIndexes.sheetAbovePageFooter}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      h={95}
      bgColor="#FFF"
    >
      <Sheet.Overlay
        animation="lazy"
        enterStyle={{ opacity: 0 }}
        exitStyle={{ opacity: 0 }}
        style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
      />
      {isOpen && (
        <YStack flex={1} backgroundColor="#fff">
          {/* header */}
          <XStack px={18} py={14} ai="center" gap={12} style={{ flex: "0 0 auto" }}>
            <Stack onPress={handleClose} cursor="pointer" width={26} pressStyle={{ opacity: 0.5 }}>
              <IoMdClose size={22} color={colors.sub} />
            </Stack>
            <Text flex={1} fontSize={17} fontWeight="800" color={colors.text}>
              {title}
            </Text>
          </XStack>

          <YStack flex={1} px={18} pb={16} style={{ overflowY: "auto" }} gap={12}>
            <XStack gap={10}>
              <Field label="縣市" value={v.city} onChange={set("city")} placeholder="台中市" flex={1} required />
              <Field label="區" value={v.area} onChange={set("area")} placeholder="西屯區" flex={1} required />
            </XStack>
            {outOfDeliveryRange ? (
              <Text fontSize={13} color={colors.danger} fontWeight="600">
                此瓦斯行無法配送到{v.city}
                {v.area}
              </Text>
            ) : null}
            <Field label="路名 / 街道" value={v.road} onChange={set("road")} placeholder="逢甲路" required />
            <XStack gap={10}>
              <Field label="門牌號" value={v.streetNumber} onChange={set("streetNumber")} placeholder="20號" flex={1.2} required />
              <Field label="樓層" value={v.floor} onChange={set("floor")} placeholder="3" flex={1} />
              <Field label="室（選填）" value={v.room} onChange={set("room")} placeholder="" flex={1} />
            </XStack>

            {/* 定位按鈕 */}
            <XStack gap={10} mt={2}>
              <LocateButton label="使用目前位置" loading={locating} onPress={useGps} />
              <LocateButton label="在地圖上定位" loading={locating} onPress={locateByAddress} />
            </XStack>

            {/* 地圖（拖曳微調座標） */}
            <YStack borderRadius={14} overflow="hidden" borderWidth={1} borderColor={colors.border} height={240}>
              <MapPicker formFields={v as never} setValue={setValue as never} moveHintText="拖曳地圖來微調定位" onGeocodingChange={setIsMapGeocoding} />
            </YStack>
            <Text fontSize={11} color={colors.sub}>
              可拖曳地圖上的圖釘微調實際門口位置。
            </Text>

            {/* 給配送員的備註（選填）→ otherAddress → 後端 addressInfos.extra */}
            <YStack gap={6}>
              <Text fontSize={12} color={colors.sub} fontWeight="600">
                給配送員的備註（選填）
              </Text>
              <TextArea
                value={v.otherAddress}
                onChangeText={set("otherAddress")}
                placeholder="例：5 樓無電梯、巷子進來、空桶放門口"
                borderWidth={1.5}
                borderColor={colors.border}
                borderRadius={11}
                backgroundColor="#fff"
                color={colors.text}
                fontSize={15}
                minHeight={80}
              />
            </YStack>
          </YStack>

          <YStack px={18} pt={12} pb={24} style={{ flex: "0 0 auto" }}>
            <XStack
              className="primary-bg-gradient"
              borderRadius={16}
              p={14}
              ai="center"
              jc="center"
              gap={10}
              cursor={saving || outOfDeliveryRange || isMapGeocoding ? "not-allowed" : "pointer"}
              onPress={saving || outOfDeliveryRange || isMapGeocoding ? undefined : handleSave}
              opacity={saving || outOfDeliveryRange || isMapGeocoding ? 0.6 : 1}
              pressStyle={{ opacity: 0.85 }}
            >
              <Text color="white" fontSize={16} fontWeight="bold">
                {isMapGeocoding ? "定位中…" : saving ? "儲存中…" : "儲存地址"}
              </Text>
              {saving ? <Spinner size="small" color="#fff" /> : null}
            </XStack>
          </YStack>
        </YStack>
      )}
    </SheetWrapper>
  );
}

export { combinedAddress };
export type { AddAddressFormData };
